import { CalendarClock } from "lucide-react";

/**
 * PTDeadlineChip — compact chip surfacing the Portuguese
 * Concurso Nacional de Acesso ao Ensino Superior deadlines on
 * Portuguese undergraduate courses. Mirrors the `<CAODeadlineChip>`
 * and `<UCASDeadlineChip>` UX so families see the cutoff at a
 * glance on Advanced Results cards, Shortlist rows and the Degree
 * Detail application section.
 *
 * Portugal runs three concurso phases each year, published by
 * DGES (Direção-Geral do Ensino Superior). Real 2026 cycle (used
 * as the canonical template) was:
 *   • Fase 1: 20 Jul → 6 Aug  (results 23 Aug)
 *   • Fase 2: 24 Aug → 20 Sep (results 30 Sep)
 *   • Fase 3: 10 Oct → 12 Oct (results 18 Oct)
 *
 * The 2027 calendar hasn't been published yet at DGES; we roll the
 * 2026 template forward and swap in real dates once the Despacho
 * lands (planned mid-2027).
 *
 * The chip shows the NEXT open phase — its state cycles through:
 *   • upcoming: "🇵🇹 Concurso · Fase 1 · opens 20 Jul {y}"
 *   • open:     "🇵🇹 Concurso · Fase 1 · closes 6 Aug {y}"
 *   • rolls to Fase 2 as soon as Fase 1 closes, etc.
 *   • post Fase 3 close: rolls forward to next year's Fase 1
 *
 * Renders `null` for non-Portuguese / non-undergraduate rows so
 * it's safe to drop into shared card templates unconditionally.
 */

const _MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];


/** Returns the 3 concurso phases for a given cycle year. Each
 *  phase is {name, opens: Date, closes: Date}. Dates are rolled
 *  from the 2026 template — DGES tends to publish the following
 *  year's calendar in Jul, at which point this template gets a
 *  real-date override. */
function _phasesFor(cycleYear) {
  return [
    {
      name: "Fase 1",
      opens: new Date(cycleYear, 6, 20),   // 20 Jul
      closes: new Date(cycleYear, 7, 6),   //  6 Aug
    },
    {
      name: "Fase 2",
      opens: new Date(cycleYear, 7, 24),   // 24 Aug
      closes: new Date(cycleYear, 8, 20),  // 20 Sep
    },
    {
      name: "Fase 3",
      opens: new Date(cycleYear, 9, 10),   // 10 Oct
      closes: new Date(cycleYear, 9, 12),  // 12 Oct
    },
  ];
}


function _isPortugalUG(degree) {
  if (!degree) return false;
  const explicit = (degree.country || "").trim().toLowerCase();
  const uidPrefix = (degree.university_id || "").split("-")[0].toLowerCase();
  const isPT = explicit === "portugal" || (!explicit && uidPrefix === "pt");
  if (!isPT) return false;
  // Postgrad/masters have direct-to-uni deadlines, not concurso.
  const level = (degree.level || "").toLowerCase();
  if (level && level !== "undergraduate" && level !== "ug") return false;
  return true;
}


function _cycleYear(degree) {
  // Portugal's concurso runs in the CALENDAR year of the intake
  // (Fase 1 opens Jul, intake starts Sep of the same year).
  const intake = (degree?.intake || "").toString();
  const m = intake.match(/^(\d{4})/);
  if (!m) return null;
  return parseInt(m[1], 10);
}


/** Picks the next phase to surface. Returns {phase, state, cycleYear}.
 *  state ∈ {"open","upcoming","rolled_next_year"}. */
export function _pickPhase(cycleYear, now) {
  const phases = _phasesFor(cycleYear);
  // 1. Are we inside an open window?
  for (const p of phases) {
    if (now >= p.opens && now <= _endOfDay(p.closes)) {
      return { phase: p, state: "open", cycleYear };
    }
  }
  // 2. Any upcoming phase this cycle?
  for (const p of phases) {
    if (now < p.opens) return { phase: p, state: "upcoming", cycleYear };
  }
  // 3. Post-Fase-3 — roll to next year's Fase 1.
  const nextYear = cycleYear + 1;
  const next = _phasesFor(nextYear)[0];
  return { phase: next, state: "rolled_next_year", cycleYear: nextYear };
}


function _endOfDay(d) {
  const e = new Date(d);
  e.setHours(23, 59, 59, 999);
  return e;
}


function _fmtDate(d) {
  return `${d.getDate()} ${_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}


export function PTDeadlineChip({ degree, testid, now = new Date() }) {
  if (!_isPortugalUG(degree)) return null;
  const cycleYear = _cycleYear(degree);
  if (!cycleYear) return null;
  const { phase, state } = _pickPhase(cycleYear, now);

  // Copy varies by state so the chip always shows the NEXT thing
  // the family needs to act on.
  const label =
    state === "open"
      ? `Concurso · ${phase.name} · closes ${_fmtDate(phase.closes)}`
      : `Concurso · ${phase.name} · opens ${_fmtDate(phase.opens)}`;

  const tooltip =
    state === "open"
      ? `Portugal's Concurso Nacional de Acesso ao Ensino Superior — ${phase.name} is OPEN. Submit at dges.gov.pt before ${_fmtDate(phase.closes)}.`
      : state === "upcoming"
      ? `Portugal's national admissions concurso — ${phase.name} opens ${_fmtDate(phase.opens)} and closes ${_fmtDate(phase.closes)} at dges.gov.pt.`
      : `The current concurso cycle has closed. Next opportunity: ${phase.name} of the ${_fmtDate(phase.opens)} cycle.`;

  // Green (open) / neutral (upcoming) tinting so families spot the
  // action-required state without hunting.
  const tint =
    state === "open"
      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
      : "bg-green-50/60 border-green-200 text-green-900";

  return (
    <span
      data-testid={testid || "pt-deadline-chip"}
      className={`inline-flex items-center gap-1.5 rounded-full border ${tint} text-[11px] font-medium px-2.5 py-1`}
      title={tooltip}
    >
      <span aria-hidden>🇵🇹</span>
      <CalendarClock className="w-3 h-3" aria-hidden />
      <span>{label}</span>
    </span>
  );
}


export default PTDeadlineChip;


// Exported for pytest-lite / vitest smoke tests.
export const _internals = { _isPortugalUG, _cycleYear, _phasesFor, _pickPhase };
